import { TimePicker } from 'components/TimePicker';
import { useAppDispatch } from 'hooks/hooks';
import { change } from 'hooks/notificationsSlice';
import React, { useCallback, useState } from 'react';
import { View, Text, Pressable, TextInput, StyleSheet } from 'react-native';
import { SelectList } from 'react-native-dropdown-select-list';

import { Coordinate } from 'types/map';
import { NotificationType } from 'types/notification';
import { dataIsValid } from 'utils/helpers';

type Props = {
  notifications: NotificationType[];
  styles: any;
  saveNotification: (notification: NotificationType) => Promise<void>;
  deleteNotification: (notification: NotificationType) => void;
  hasChanged: (route: NotificationType) => boolean;
  cancelChanges: (notification: NotificationType) => void;
  addNotification: () => void;
};

const levels = [
  {
    key: 0,
    value: 'Top',
  },
  {
    key: 1,
    value: 'High',
  },
  {
    key: 2,
    value: 'Medium',
  },
  {
    key: 3,
    value: 'Low',
  },
];

const occurrence = [
  {
    key: 'once',
    value: 'Once',
  },
  {
    key: 'daily',
    value: 'Daily',
  },
];

function NotificationRow({
  notification,
  styles,
  deleteNotification,
  hasChanged,
  cancelChanges,
  saveNotification,
}: {
  notification: NotificationType;
  styles: any;
  deleteNotification: (notification: NotificationType) => void;
  hasChanged: (route: NotificationType) => boolean;
  cancelChanges: (notification: NotificationType) => void;
  saveNotification: (notification: NotificationType) => Promise<void>;
}) {
  const dispatch = useAppDispatch();
  const form = {
    title: {
      required: true,
      errorMessage: 'Title is required.',
      error: null,
    },
    description: {
      required: true,
      errorMessage: 'Description is required.',
      error: null,
    },
    occurrence: {
      required: true,
      errorMessage: 'Occurrence is required.',
      error: null,
    },
  };
  const changed = hasChanged(notification);
  const [formValidation, setFormValidation] = useState({ ...form });

  const onDataChange = useCallback(
    (key: string, value: string | Coordinate[] | []) => {
      if (value !== notification[key]) {
        const newValue = {
          ...notification,
          [key]: value,
        };
        dispatch(change({ notification: newValue }));
      }
    },
    [notification, dispatch],
  );

  return (
    <View style={styles.row}>
      <View>
        <Text style={styles.label}>Title:</Text>
        <TextInput
          style={[
            styles.input,
            formValidation.title.error ? styles.inputError : null,
          ]}
          value={notification.title}
          onChangeText={(text) => onDataChange('title', text)}
        />
        {formValidation.title.error && (
          <Text style={styles.error}>{formValidation.title.errorMessage}</Text>
        )}

        <Text style={styles.label}>Description:</Text>
        <TextInput
          style={[
            styles.input,
            formValidation.description.error ? styles.inputError : null,
          ]}
          multiline
          numberOfLines={3}
          value={notification.description}
          onChangeText={(text) => onDataChange('description', text)}
        />
        {formValidation.description.error && (
          <Text style={styles.error}>
            {formValidation.description.errorMessage}
          </Text>
        )}

        <Text style={styles.label}>Priority level:</Text>
        <SelectList
          data={levels}
          setSelected={(val) => onDataChange('level', val)}
          boxStyles={style.dropdown}
          dropdownStyles={styles.input}
          defaultOption={levels.find((l) => l.key === notification.level)}
        />

        <Text style={styles.label}>Occurrence:</Text>
        <SelectList
          data={occurrence}
          setSelected={(val) => onDataChange('occurrence', val)}
          boxStyles={style.dropdown}
          dropdownStyles={styles.input}
          defaultOption={occurrence.find(
            (l) => l.key === notification.occurrence,
          )}
        />
        {formValidation.occurrence.error && (
          <Text style={styles.error}>
            {formValidation.occurrence.errorMessage}
          </Text>
        )}

        <View>
          <Text style={styles.label}>When?</Text>
          <TimePicker
            value={notification.occur}
            onChange={(val) => onDataChange('occur', val)}
          />
        </View>
      </View>

      <View style={styles.inlineButtonContainer}>
        <Pressable onPress={() => deleteNotification(notification)}>
          <Text style={[styles.inlineButton, styles.error]}>Remove</Text>
        </Pressable>
        {changed && (
          <>
            <Pressable
              onPress={() => {
                setFormValidation({ ...form });
                cancelChanges(notification);
              }}
            >
              <Text style={[styles.inlineButton]}>Cancel</Text>
            </Pressable>
            <Pressable
              onPress={() => {
                if (
                  dataIsValid(formValidation, setFormValidation, notification)
                ) {
                  saveNotification(notification);
                }
              }}
            >
              <Text style={[styles.inlineButton, styles.success]}>Save</Text>
            </Pressable>
          </>
        )}
      </View>
    </View>
  );
}

export function EditNotifications({
  notifications,
  styles,
  saveNotification,
  deleteNotification,
  hasChanged,
  cancelChanges,
  addNotification,
}: Props) {
  return (
    <View>
      {notifications && (
        <View>
          {notifications.map((notification) => (
            <NotificationRow
              key={notification.id}
              notification={notification}
              styles={styles}
              deleteNotification={deleteNotification}
              cancelChanges={cancelChanges}
              hasChanged={hasChanged}
              saveNotification={saveNotification}
            />
          ))}
        </View>
      )}
      <View style={styles.buttonContainer}>
        <Pressable onPress={() => addNotification()} style={styles.button}>
          <Text style={styles.buttonText}>Add Notification</Text>
        </Pressable>
      </View>
    </View>
  );
}

const style = StyleSheet.create({
  dropdown: {
    marginBottom: 10,
    borderRadius: 0,
    paddingVertical: 5,
    paddingHorizontal: 5,
  },
});
